import {
  END_USER_TERMS,
  PRIVACY_POLICY,
} from 'shared/constants/urls';
import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';

import { useMutation, useQuery } from 'hooks/apollo';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import { Button, Card, Form, Input, Result, Row, Col, Checkbox } from 'antd';
import useAuth from 'hooks/useAuth';

import Hero from 'components/Hero';
import Loading from 'components/Loading';
import useFormatMessage from 'hooks/useFormatMessage';
import useGlobalContext from 'hooks/useGlobalContext';
import SetupPaymentButton from 'components/SetupPaymentButton';
import PhoneInput, { validPhoneNumber } from 'components/PhoneInput';
import messages from './messages';

const USER_QUERY = gql`
  query UserRegistration($id: ID!) {
    userRegistration(id: $id) {
      id
      email
      status
      accountRegistration {
        id
        payment
      }
    }
  }
`;

const REGISTER_MUTATION = gql`
  mutation RegisterUser($input: RegisterInput!) {
    registerUser(input: $input)
  }
`;

const booleanValidation = {
  required: true,
  transform: value => (value || undefined),
  type: 'boolean',
};

const RegisterUserForm = ({ user, onFinish, loading }) => {
  const $t = useFormatMessage();
  return (
    <Form onFinish={onFinish} layout="vertical" initialValues={{ payment: !!(!user.accountRegistration?.paid || user.accountRegistration.paymentType) }}>
      <p><FormattedMessage {...messages.title} /></p>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="firstname"
            label="First name"
            rules={[{ required: true, message: $t(messages.nameRequired) }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="lastname"
            label="Last name"
            rules={[{ required: true, message: $t(messages.nameRequired) }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="phone"
            label="Mobile number"
            rules={[validPhoneNumber]}
          >
            <PhoneInput />
          </Form.Item>
        </Col>

        <Col span={12}>
          {user.accountRegistration && (
            <Form.Item
              name="accountName"
              label="Organization"
              rules={[{ required: true, message: $t(messages.accountNameRequired) }]}
            >
              <Input autoComplete="organization" />
            </Form.Item>
          )}
        </Col>
        <Col span={12}>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: $t(messages.passwordRequired) }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="confirm"
            label="Confirm password"
            dependencies={['password']}
            rules={[
              { required: true, message: $t(messages.confirmRequired) },
              ({ getFieldValue }) => ({
                async validator(_, value) {
                  if (value && getFieldValue('password') !== value) {
                    throw new Error($t(messages.passwordMissmatch));
                  }
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="tos" valuePropName="checked" rules={[{ ...booleanValidation, message: 'You need to accept our terms of service' }]}>
        <Checkbox>
          I have read and agree to the
          {' '}
          <a href={PRIVACY_POLICY} target="_blank">Privacy policy</a>
          {' '}
          and the
          {' '}
          <a href={END_USER_TERMS} target="_blank">Terms of service</a>
        </Checkbox>
      </Form.Item>
      <Form.Item>
        By activating my account I agree to receive marketing communication from Extellio. You can opt-out of this later.
      </Form.Item>
      <Button block type="primary" htmlType="submit" loading={loading}>
        <FormattedMessage {...messages.register} />
      </Button>
    </Form>
  );
};

const RegisterUser = () => {
  const { params: { userId } } = useRouteMatch();
  const { formatMessage: $t } = useIntl();
  const { message } = useGlobalContext();
  const history = useHistory();
  const { login } = useAuth();
  const [step, setStep] = useState();

  const { data: { userRegistration } = {}, loading } = useQuery(USER_QUERY, {
    variables: { id: userId },
  });

  const [registerUser, { loading: registering }] = useMutation(REGISTER_MUTATION);

  useEffect(() => {
    if (userRegistration) {
      setStep(userRegistration.accountRegistration?.payment ? 0 : 1);
    }
  }, [userRegistration]);

  const onFinish = async (values) => {
    try {
      await registerUser({
        variables: {
          input: {
            user: {
              id: userId,
              firstname: values.firstname,
              lastname: values.lastname,
              phone: values.phone,
              password: values.password,
            },
            account: userRegistration.accountRegistration ? {
              id: userRegistration.accountRegistration.id,
              name: values.accountName,
            } : undefined,
          },
        },
      });

      const loginResult = await login(userRegistration.email, values.password);
      if (loginResult?.mfa) {
        history.push('/login', {
          email: userRegistration.email,
          mfaPending: true,
        });
      }
    } catch (error) {
      console.log(error);
      message.error($t(messages[error.message] || messages.registrationFailed));
    }
  };

  const onPaymentSuccess = () => {
    setStep(1);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Hero branded>
      <Card title="Account registration" style={{ width: 600 }}>
        {userRegistration ? (
          <>
            {step === 0 && (
              <Result
                status="info"
                title="Payment"
                subTitle={(
                  <>
                    You ordered a paid plan. Please register your credit card for payment.
                  </>
                )}
                extra={(
                  <SetupPaymentButton customerId={userRegistration.accountRegistration.id} onSuccess={onPaymentSuccess} />
                )}
              />
            )}
            {step === 1 && (
              <RegisterUserForm user={userRegistration} onFinish={onFinish} loading={loading || registering} />
            )}
          </>
        ) : (
          <Result
            status="info"
            title={$t(messages.invalidLink)}
            subTitle={(
              <>
                <p>
                  <FormattedMessage {...messages.invalidLinkDesc} />
                </p>
                <Link to="/forgot-password">
                  <Button type="primary">
                    {$t(messages.resetPassword)}
                  </Button>
                </Link>
              </>
            )}
          />
        )}
      </Card>
    </Hero>
  );
};

export default RegisterUser;
